import styled, { css } from 'vue-styled-components';
import { mapState } from 'vuex';

import {
    SelectedBox,
    DefaultDropdownBox,
} from '@/components/Common/DropdownBox';

import NextIcon from '@svgIcons/next.svg';

import { GRAY } from '@/styles/common.styled';

import FilterFormatterMixin from '@/components/Dashboard/common/UaFilterSettingPanel/mixin';

import { isValueExist } from '@/utils/utils';
import { checkHasLevel } from '@/components/Dashboard/common/Filter/utils';
import { FILTER_KEYS } from '@/utils/consts';
import { FIELD_OPTIONS } from '@/components/Dashboard/common/SearchBar/filters/const';
import {
    getRefinedField,
    getRefinedLevel,
} from '@/components/Common/FilterInfo/utils';

const { GENERIC } = FILTER_KEYS;
const FULL_FIELDS = Object.keys(FIELD_OPTIONS);

export default {
    name: 'FilterDropdown',
    mixins: [FilterFormatterMixin],
    props: {
        filterData: {
            type: Object,
            default: () => ({}),
        },
        handleConfirmField: {
            type: Function,
        },
        handleConfirmLevel: {
            type: Function,
        },
        handleGenericLevelInput: {
            type: Function,
            default: () => {},
        },
        genericLevelOptions: {
            type: Array,
            default: () => [],
        },
        breakdownListByChannel: {
            type: Object,
            default: () => ({}),
        },
        categoryListByPrefix: {
            type: Object,
            default: () => ({}),
        },
        disabledFieldOptions: {
            type: Array,
            default: () => [],
        },
        discardFieldOptions: {
            type: Array,
            default: () => [],
        },
        discardGenericLevelOptions: {
            type: Array,
            default: () => [],
        },
        groupSourceType: {
            type: String,
        },
        genericLevelTranslator: {
            type: Function,
            default: R.identity,
        },
        genericBreakdownTranslator: {
            type: Function,
            default: R.identity,
        },
    },
    data() {
        return {
            fieldVisible: false,
            nestedLevelVisible: R.compose(
                R.mapObjIndexed((_) => false),
                R.pickBy(
                    (val, key) =>
                        R.propSatisfies(isValueExist, 'level', val) ||
                        R.equals(GENERIC, key)
                )
            )(FIELD_OPTIONS),
            genericVisible: false,
            breakdownList: [],
            selectedBreakdownChannel: undefined,
        };
    },
    watch: {
        isBreakdownFilterLoading: {
            handler() {
                const { breakdownListByChannel, selectedBreakdownChannel } =
                    this;

                if (!selectedBreakdownChannel) return;

                this.breakdownList = R.propOr(
                    [],
                    selectedBreakdownChannel,
                    breakdownListByChannel
                );
            },
        },
    },
    computed: {
        ...mapState('newDashboard', ['isBreakdownFilterLoading']),
    },
    render() {
        const {
            discardFieldOptions: _discardFieldOptions,
            disabledFieldOptions,
            genericLevelOptions,
            discardGenericLevelOptions,
            filterData,
            fieldTranslator,
            breakdownListByChannel,
            categoryListByPrefix,
            isBreakdownFilterLoading,
            groupSourceType,
            nestedLevelVisible,
        } = this;

        const refinedField = getRefinedField(filterData);
        const refinedLevel = getRefinedLevel(filterData);

        const levelTranslator = this.getLevelTranslator(refinedField);

        const fieldText = R.when(isValueExist, fieldTranslator)(refinedField);
        const levelText = R.when(isValueExist, levelTranslator)(refinedLevel);
        const selectedOptionText = refinedLevel
            ? `${fieldText}: ${levelText}`
            : fieldText;

        const discardFieldOptions =
            genericLevelOptions.length === discardGenericLevelOptions.length
                ? [..._discardFieldOptions, GENERIC]
                : _discardFieldOptions;
        const fieldOptions = R.without(discardFieldOptions, FULL_FIELDS);

        return (
            <Wrapper class="selected-box-wrapper field-dropdown">
                <SelectedBox
                    placeholder={this.$t('NEW_DASHBOARD.dropdown_placeholder')}
                    selectedOption={selectedOptionText}
                    tooltipGenerator={(text) => {
                        const isBreakdown = R.test(/^breakdown/i, text);

                        return isBreakdown ? text : '';
                    }}
                    onClick={() => {
                        if (groupSourceType) {
                            const categories = R.propOr(
                                [],
                                groupSourceType,
                                categoryListByPrefix
                            );

                            this.breakdownList = R.compose(
                                R.flatten,
                                R.map(
                                    R.propOr([], R.__, breakdownListByChannel)
                                )
                            )(categories);

                            this.genericVisible = !this.genericVisible;
                        } else {
                            if (refinedField === GENERIC && !refinedLevel) {
                                this.genericVisible = !this.genericVisible;
                            } else {
                                if (!this.genericVisible)
                                    this.fieldVisible = !this.fieldVisible;
                                this.genericVisible = false;
                            }
                        }
                    }}
                />
                <DefaultDropdownBox
                    customCss={fieldDropdownCss}
                    width={221}
                    height={370}
                    showSelected={false}
                    selectedOption={refinedField}
                    isVisible={this.fieldVisible}
                    optionList={fieldOptions}
                    customItemRenderer={(text) => {
                        const disabled = R.includes(text, disabledFieldOptions);

                        const levelTranslator = this.getLevelTranslator(text);
                        const levelOptions =
                            text === GENERIC
                                ? R.compose(
                                      R.sortBy(this.genericLevelTranslator),
                                      R.without(discardGenericLevelOptions)
                                  )(genericLevelOptions)
                                : Object.values(
                                      R.pathOr(
                                          {},
                                          [text, 'level'],
                                          FIELD_OPTIONS
                                      )
                                  );
                        const hasLevel =
                            checkHasLevel(text) || text === GENERIC;

                        const fieldItem$ = this.$refs[`fieldItem-${text}`]?.$el;
                        const top = fieldItem$?.offsetTop ?? 0;

                        return (
                            <ItemWrapper
                                ref={`fieldItem-${text}`}
                                class={disabled ? 'disabled' : ''}
                            >
                                <Text class="adriel-ellipsis">
                                    {this.fieldTranslator(text)}
                                </Text>
                                {hasLevel && <NextIcon v-view-box />}
                                {hasLevel && (
                                    <DefaultDropdownBox
                                        selectedOption={refinedLevel}
                                        width={160}
                                        customCss={getNestedDropdownCss(top)}
                                        class="nested-dropdown-wrapper"
                                        isVisible={
                                            this.nestedLevelVisible[text]
                                        }
                                        optionList={levelOptions}
                                        translator={
                                            text === GENERIC
                                                ? this.genericLevelTranslator
                                                : levelTranslator
                                        }
                                        onConfirm={(value) => {
                                            if (text === GENERIC) {
                                                this.selectedBreakdownChannel =
                                                    value;
                                                this.breakdownList = R.propOr(
                                                    [],
                                                    value,
                                                    breakdownListByChannel
                                                );

                                                this.genericVisible = true;
                                            } else {
                                                this.handleConfirmLevel(value);
                                            }
                                            this.nestedLevelVisible =
                                                R.mapObjIndexed(
                                                    (_) => false,
                                                    nestedLevelVisible
                                                );
                                            this.fieldVisible = false;
                                        }}
                                        onClickOutside={() => {
                                            this.nestedLevelVisible =
                                                R.mapObjIndexed(
                                                    (_) => false,
                                                    nestedLevelVisible
                                                );
                                        }}
                                        appendToBody={false}
                                    />
                                )}
                            </ItemWrapper>
                        );
                    }}
                    onConfirm={(field) => {
                        const disabled = R.includes(
                            field,
                            disabledFieldOptions
                        );
                        if (disabled) return;

                        this.nestedLevelVisible = R.mapObjIndexed(
                            (_, key) => key === field,
                            nestedLevelVisible
                        );

                        if (!(checkHasLevel(field) || field === GENERIC)) {
                            this.fieldVisible = false;
                        }

                        this.handleConfirmField(field);
                    }}
                    onClickOutside={() => {
                        this.fieldVisible = false;
                    }}
                />
                <DefaultDropdownBox
                    isLoading={isBreakdownFilterLoading}
                    class="searchbar-wrapper"
                    isVisible={this.genericVisible}
                    optionList={this.breakdownList}
                    showSelected={false}
                    selectedOption={refinedField}
                    translator={this.genericTranslator}
                    onConfirm={(value) => {
                        this.handleGenericLevelInput(value);
                        this.genericVisible = false;
                    }}
                    onClickOutside={() => (this.genericVisible = false)}
                    hasSearchBar={true}
                />
            </Wrapper>
        );
    },
};

const Wrapper = styled.div`
    .option-item > p {
        width: 100%;
    }

    .selected-box > h1 {
        flex: 1;
    }
`;

const ItemWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Text = styled.div`
    flex: 1;
`;

const fieldDropdownCss = css`
    margin-top: 5px;
    border: 1px solid ${GRAY(30)};
    border-radius: 2px;

    li:has(.disabled) {
        cursor: not-allowed;
    }
`;

const getNestedDropdownCss = (top) => css`
    position: absolute;
    top: ${top - 10}px;
    left: 219px;
    border: 1px solid ${GRAY(30)};
    border-radius: 2px;

    & > div:has(ul) {
        width: 100%;
    }
`;
